import Icon from '@/components/ui/AppIcon';

interface ContactMethod {
  icon: string;
  title: string;
  description: string;
  value: string;
  action: string;
  actionLink: string;
  available: string;
}

interface ContactMethodsProps {
  currentLanguage: 'fr' | 'de';
}

const ContactMethods = ({ currentLanguage }: ContactMethodsProps) => {
  const content = {
    fr: {
      sectionTitle: "Plusieurs façons de nous contacter",
      sectionSubtitle: "Choisissez le canal qui vous convient le mieux",
      methods: [
        {
          icon: "PhoneIcon",
          title: "Téléphone",
          description: "Appelez-nous pour une réponse immédiate",
          value: "0041762126506",
          action: "Appeler maintenant",
          actionLink: "tel:0041762126506",
          available: "Lun-Dim: 7h00-20h00"
        },
        {
          icon: "EnvelopeIcon",
          title: "Email",
          description: "Envoyez-nous un message détaillé",
          value: "servicesrouissi@gmail.com",
          action: "Envoyer un email",
          actionLink: "mailto:servicesrouissi@gmail.com",
          available: "Réponse sous 24h"
        },
        {
          icon: "ChatBubbleLeftEllipsisIcon",
          title: "WhatsApp",
          description: "Discutez avec nous instantanément",
          value: "0041762126506",
          action: "Ouvrir WhatsApp",
          actionLink: "https://wa.me/41762126506",
          available: "Disponible 24/7"
        },
        {
          icon: "MapPinIcon",
          title: "Visite au bureau",
          description: "Rencontrez-nous en personne",
          value: "Rue de Gerardmer 24, 2400 Le Locle, Suisse",
          action: "Obtenir l\'itinéraire",
          actionLink: "https://maps.google.com/?q=Rue+de+Gerardmer+24+2400+Le+Locle+Suisse",
          available: "Lun-Ven: 8h00-17h00"
        }
      ] as ContactMethod[]
    },
    de: {
      sectionTitle: "Mehrere Möglichkeiten, uns zu kontaktieren",
      sectionSubtitle: "Wählen Sie den Kanal, der Ihnen am besten passt",
      methods: [
        {
          icon: "PhoneIcon",
          title: "Telefon",
          description: "Rufen Sie uns für eine sofortige Antwort an",
          value: "0041762126506",
          action: "Jetzt anrufen",
          actionLink: "tel:0041762126506",
          available: "Mo-So: 7:00-20:00"
        },
        {
          icon: "EnvelopeIcon",
          title: "E-Mail",
          description: "Senden Sie uns eine detaillierte Nachricht",
          value: "servicesrouissi@gmail.com",
          action: "E-Mail senden",
          actionLink: "mailto:servicesrouissi@gmail.com",
          available: "Antwort innerhalb von 24 Stunden"
        },
        {
          icon: "ChatBubbleLeftEllipsisIcon",
          title: "WhatsApp",
          description: "Chatten Sie sofort mit uns",
          value: "0041762126506",
          action: "WhatsApp öffnen",
          actionLink: "https://wa.me/41762126506",
          available: "Verfügbar 24/7"
        },
        {
          icon: "MapPinIcon",
          title: "Bürobesuch",
          description: "Treffen Sie uns persönlich",
          value: "Rue de Gerardmer 24, 2400 Le Locle, Schweiz",
          action: "Route abrufen",
          actionLink: "https://maps.google.com/?q=Rue+de+Gerardmer+24+2400+Le+Locle+Schweiz",
          available: "Mo-Fr: 8:00-17:00"
        }
      ] as ContactMethod[]
    }
  };

  const text = content[currentLanguage];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            {text.sectionTitle}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {text.sectionSubtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {text.methods.map((method, index) => (
            <div
              key={index}
              className="bg-card rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-border group"
            >
              <div className="flex items-center justify-center w-14 h-14 bg-primary/10 rounded-xl mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <Icon
                  name={method.icon as any}
                  size={28}
                  variant="outline"
                  className="text-primary group-hover:text-white transition-colors duration-300"
                />
              </div>

              <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                {method.title}
              </h3>

              <p className="text-sm text-muted-foreground mb-4">
                {method.description}
              </p>

              <p className="text-base font-medium text-foreground mb-2 break-words">
                {method.value}
              </p>

              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Icon name="ClockIcon" size={16} variant="outline" />
                <span>{method.available}</span>
              </div>

              <a
                href={method.actionLink}
                className="inline-flex items-center gap-2 text-primary hover:text-secondary font-medium transition-colors duration-300"
              >
                <span>{method.action}</span>
                <Icon name="ArrowRightIcon" size={16} variant="outline" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactMethods;